<script setup>
import { WButton, WHelper } from '@alice-health/wonderland-vue'
import differenceInSeconds from 'date-fns/differenceInSeconds'
import nextMonday from 'date-fns/nextMonday'
import { computed, onBeforeUnmount, reactive } from 'vue'

import { HIDE_CERTIFICATION_MODAL, useCookie } from '@/libs/cookie'
import Logger, { EventName } from '@/wrappers/logger'
import { useStore } from '@/wrappers/store'

import useCertificateService from '../../../services/certificate.hook'
import { HCertificationInput, HCertificationResult } from '../atoms'

const { setCookie } = useCookie(HIDE_CERTIFICATION_MODAL)
const certificateService = useCertificateService()
const { mapGetters } = useStore()
const { user } = mapGetters('user')

const props = defineProps({
  context: {
    type: String,
    default: null
  },
  mini: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

const state = reactive({
  action: null,
  loading: false,
  result: null,
  token: null,
  invalidToken: false
})

const shouldShowMini = computed(() => props.mini || null)

onBeforeUnmount(() => {
  let resultAction

  if (state.action !== 'login') {
    resultAction = 'CANCELLED'
  } else if (state.result?.certificated) {
    resultAction = 'SUCCESS'
  } else {
    resultAction = 'ERROR'
  }

  Logger.addLog(EventName.CERTIFICATION_INTERACTION, {
    data: {
      context: props.context || 'default',
      action: state.action,
      value: resultAction
    }
  })
})

const updateToken = token => {
  state.token = token
}
const handleUserToken = () => {
  if (isInvalidToken(state.token)) {
    state.invalidToken = true
    return
  }
  authenticate(state.token)
}
const isInvalidToken = token => {
  const tokenAsString = String(token)
  return !/^\d+$/.test(tokenAsString) || tokenAsString.length !== 6
}
const authenticate = async userToken => {
  state.loading = true
  state.action = 'login'
  const result = await certificateService.certificateUser({
    userToken,
    userId: user.value.id
  })
  state.result = result
  state.loading = false
}
const handleClose = () => {
  emit('close', state.result)
}
const handleCancel = () => {
  setCookie(true, {
    'max-age': differenceInSeconds(nextMonday(new Date()), new Date())
  })
  state.action = 'cancel'
  emit('close', null)
}
const openInput = () => {
  state.result = null
}
</script>

<template>
  <div class="certification" :data-mini="shouldShowMini">
    <template v-if="state.result">
      <HCertificationResult :result="state.result" @close="handleClose" />
      <WButton
        v-if="state.result.error"
        variant="primary"
        size="small"
        @click.prevent.stop="openInput"
      >
        Tentar novamente
      </WButton>
    </template>
    <template v-else>
      <form @submit.prevent="handleUserToken">
        <HCertificationInput :loading="state.loading" @input="updateToken" />
        <WHelper v-show="state.invalidToken" mode="error">
          Token inválido. Tente novamente.
        </WHelper>
        <hr v-if="!mini" class="certification__hr" />
        <div class="certification__buttons">
          <WButton
            icon="icSend1"
            :loading="state.loading"
            :disabled="!state.token"
            variant="primary"
            size="small"
            type="button"
            @click="handleUserToken"
          >
            Renovar sessão
          </WButton>
          <WButton
            v-if="!mini"
            :disabled="state.loading"
            size="small"
            variant="tertiary"
            @click="handleCancel"
          >
            Cancelar
          </WButton>
        </div>
      </form>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.certification {
  &__hr {
    margin-top: var(--gl-spacing-12);
    margin-right: calc(var(--gl-spacing-10) * -1);
    margin-left: calc(var(--gl-spacing-10) * -1);
    background-color: var(--gl-color-shades-gray-10);
    opacity: 1;
  }
  &__buttons {
    display: flex;
    margin-top: var(--gl-spacing-06);
    gap: var(--gl-spacing-04);
  }
  [data-mini] &__buttons {
    margin-top: var(--gl-spacing-04);
  }
}
</style>
